import { Link } from "react-router-dom";
import { CAMPAIGN_HISTORY_STATUS_OPTIONS } from "../constants.js";
import { RefreshIcon } from "../components/Icons.jsx";
import { InventoryMetricCards } from "../components/InventoryMetricCards.jsx";
import MetricInfoTooltip from "../components/MetricInfoTooltip.jsx";
import { formatEpoch } from "../utils/formatters.js";
import { getCampaignProgress, getCampaignStatusLabel } from "../utils/history.js";
import {
  CAMPAIGN_HISTORY_HELP,
  CAMPAIGN_PERFORMANCE_HELP,
} from "../utils/metricDefinitions.js";
import { formatRate, getRedemptionMetrics } from "../utils/redemptionMetrics.js";
import "../styles/history.css";

export default function HistoryPage({
  historyFilters,
  metricsState,
  campaignHistoryState,
  onHistoryFilterChange,
  onApplyFilters,
  onClearFilters,
  onHistoryRefresh,
  onHistoryPreviousPage,
  onHistoryNextPage,
  onHistoryPageSelect,
}) {
  const campaigns = campaignHistoryState.campaigns || [];
  const hasFilters = Boolean(
    historyFilters.designator ||
    historyFilters.status ||
    historyFilters.createdFrom ||
    historyFilters.createdTo
  );
  const invalidDateRange = Boolean(
    historyFilters.createdFrom &&
    historyFilters.createdTo &&
    historyFilters.createdFrom > historyFilters.createdTo
  );
  const pageSize = Math.max(1, Number(campaignHistoryState.pageSize) || 10);
  const totalPages =
    campaignHistoryState.totalCount !== null
      ? Math.max(1, Math.ceil(Number(campaignHistoryState.totalCount || 0) / pageSize))
      : null;
  const pageNumbers = [
    ...new Set([
      ...campaignHistoryState.pages.map((page) => page.pageNumber),
      ...(campaignHistoryState.hasMore ? [campaignHistoryState.currentPage + 1] : []),
    ]),
  ].sort((left, right) => left - right);

  return (
    <main className="cg-page-shell">
      <div className="cg-page-frame">
        <section className="cg-panel cg-page-card">
          <div className="cg-panel-header cg-history-panel-header">
            <div>
              <span className="cg-kicker">History</span>
              <h2>Current code inventory</h2>
            </div>
            <button type="button" className="cg-page-refresh-button" onClick={onHistoryRefresh}>
              <RefreshIcon />
              Refresh data
            </button>
          </div>

          {metricsState.error ? (
            <div className="cg-inline-feedback cg-inline-feedback-error">
              <span>{metricsState.error}</span>
              <button type="button" onClick={onHistoryRefresh}>Try again</button>
            </div>
          ) : null}
          <InventoryMetricCards inventory={metricsState.inventory} loading={metricsState.loading} />

          <div className="cg-panel-header cg-history-subheader">
            <div>
              <span className="cg-kicker">Campaigns</span>
              <h2>Campaign history and code inventory</h2>
              {campaignHistoryState.totalCount !== null ? (
                <p className="cg-muted-text">
                  {campaignHistoryState.totalCount.toLocaleString()} {hasFilters ? "matching " : ""}campaigns
                </p>
              ) : null}
            </div>
          </div>

          <form
            className="cg-history-filter-panel"
            role="search"
            onSubmit={(event) => {
              event.preventDefault();
              if (!invalidDateRange) onApplyFilters();
            }}
          >
            <label className="cg-history-designator-filter">
              Campaign designator
              <input
                type="text"
                value={historyFilters.designator}
                onChange={(event) => onHistoryFilterChange("designator", event.target.value.toUpperCase())}
                placeholder="e.g. AMARE"
              />
            </label>
            <label>
              Campaign status
              <select
                value={historyFilters.status}
                onChange={(event) => onHistoryFilterChange("status", event.target.value)}
              >
                {CAMPAIGN_HISTORY_STATUS_OPTIONS.map((option) => (
                  <option key={option.value || "all"} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
            <label>
              Created from
              <input
                type="date"
                value={historyFilters.createdFrom}
                max={historyFilters.createdTo || undefined}
                onChange={(event) => onHistoryFilterChange("createdFrom", event.target.value)}
              />
            </label>
            <label>
              Created to
              <input
                type="date"
                value={historyFilters.createdTo}
                min={historyFilters.createdFrom || undefined}
                onChange={(event) => onHistoryFilterChange("createdTo", event.target.value)}
              />
            </label>
            <div className="cg-history-filter-buttons">
              <button type="submit" disabled={invalidDateRange || campaignHistoryState.loading}>
                Apply filters
              </button>
              <button
                type="button"
                className="cg-history-clear-button"
                onClick={onClearFilters}
                disabled={!hasFilters || campaignHistoryState.loading}
              >
                Clear
              </button>
            </div>
            {invalidDateRange ? (
              <p className="cg-history-filter-error">The start date must be on or before the end date.</p>
            ) : null}
          </form>

          {campaignHistoryState.error ? (
            <div className="cg-inline-feedback cg-inline-feedback-error">
              <span>{campaignHistoryState.error}</span>
              <button type="button" onClick={onHistoryRefresh}>Try again</button>
            </div>
          ) : null}

          <div className="cg-history-results">
            <div className="cg-history-table-wrap">
              <table className="cg-history-table">
                <thead>
                  <tr>
                    <th><MetricInfoTooltip label="Campaign" description={CAMPAIGN_HISTORY_HELP.campaign} align="start" /></th>
                    <th><MetricInfoTooltip label="Status" description={CAMPAIGN_HISTORY_HELP.status} /></th>
                    <th><MetricInfoTooltip label="Generation" description={CAMPAIGN_HISTORY_HELP.generation} /></th>
                    <th><MetricInfoTooltip label="Available" description={CAMPAIGN_HISTORY_HELP.available} /></th>
                    <th><MetricInfoTooltip label="Delivered" description={CAMPAIGN_PERFORMANCE_HELP.delivered} /></th>
                    <th><MetricInfoTooltip label="Order redemption" description={CAMPAIGN_PERFORMANCE_HELP.orderRedemption} /></th>
                    <th><MetricInfoTooltip label="Code released" description={CAMPAIGN_PERFORMANCE_HELP.codeReleased} /></th>
                    <th><MetricInfoTooltip label="Unrealized" description={CAMPAIGN_PERFORMANCE_HELP.unrealized} /></th>
                    <th><MetricInfoTooltip label="True redemption" description={CAMPAIGN_PERFORMANCE_HELP.trueRedemption} /></th>
                    <th><MetricInfoTooltip label="Last code activity" description={CAMPAIGN_HISTORY_HELP.lastActivity} /></th>
                    <th><MetricInfoTooltip label="Created" description={CAMPAIGN_HISTORY_HELP.created} align="end" /></th>
                  </tr>
                </thead>
                <tbody>
                  {campaignHistoryState.loading ? (
                    <tr><td colSpan="11">Loading campaign history...</td></tr>
                  ) : campaigns.length ? (
                    campaigns.map((campaign) => {
                      const progress = getCampaignProgress(campaign);
                      const inventory = campaign.inventory || {};
                      const performance = getRedemptionMetrics(inventory);
                      return (
                        <tr key={campaign.campaign_id}>
                          <td>
                            <Link className="cg-text-link" to={`/cologuard/campaigns/${campaign.campaign_id}`}>
                              {campaign.campaign_designator || "N/A"}
                            </Link>
                            <span className="cg-history-campaign-name">{campaign.campaign_name || "Unnamed campaign"}</span>
                          </td>
                          <td>{getCampaignStatusLabel(campaign.campaign_status)}</td>
                          <td>{progress.completedCodes.toLocaleString()} / {progress.requested.toLocaleString()}</td>
                          <td>{Number(inventory.available_codes || 0).toLocaleString()}</td>
                          <td>{performance.available ? performance.delivered.toLocaleString() : "N/A"}</td>
                          <td>
                            {performance.available ? (
                              <span className="cg-history-rate">
                                <strong>{formatRate(performance.orderRedemptionRate)}</strong>
                                <small>{performance.orderRedemptionCodes.toLocaleString()} of {performance.delivered.toLocaleString()}</small>
                              </span>
                            ) : "N/A"}
                          </td>
                          <td>
                            {performance.available ? (
                              <span className="cg-history-rate">
                                <strong>{formatRate(performance.codeReleasedRate)}</strong>
                                <small>{performance.codeReleasedCodes.toLocaleString()} of {performance.delivered.toLocaleString()}</small>
                              </span>
                            ) : "N/A"}
                          </td>
                          <td>
                            {performance.available ? (
                              <span className="cg-history-rate">
                                <strong>{formatRate(performance.unrealizedRate)}</strong>
                                <small>{performance.unrealizedCodes.toLocaleString()} of {performance.delivered.toLocaleString()}</small>
                              </span>
                            ) : "N/A"}
                          </td>
                          <td>
                            {performance.available ? (
                              <span className="cg-history-rate">
                                <strong>{formatRate(performance.trueRedemptionRate)}</strong>
                                <small>{performance.trueRedemptionCodes.toLocaleString()} of {performance.delivered.toLocaleString()}</small>
                              </span>
                            ) : "N/A"}
                          </td>
                          <td>{formatEpoch(inventory.updated_at)}</td>
                          <td>{formatEpoch(campaign.created_at)}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="11">
                        {hasFilters
                          ? "No campaigns match these filters. Adjust or clear the filters and try again."
                          : "No campaigns have been created yet."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {campaignHistoryState.pages.length ? (
              <div className="cg-history-pagination">
                <button
                  type="button"
                  className="cg-history-next-button"
                  onClick={onHistoryPreviousPage}
                  disabled={campaignHistoryState.currentPage === 1 || campaignHistoryState.loadingPage}
                >
                  Previous
                </button>
                <div className="cg-history-page-buttons">
                  {pageNumbers.map((pageNumber) => (
                    <button
                      key={pageNumber}
                      type="button"
                      className={`cg-history-page-button ${campaignHistoryState.currentPage === pageNumber ? "active" : ""}`}
                      onClick={() => onHistoryPageSelect(pageNumber)}
                      disabled={campaignHistoryState.loadingPage}
                      aria-label={`Page ${pageNumber}`}
                      aria-current={campaignHistoryState.currentPage === pageNumber ? "page" : undefined}
                    >
                      {pageNumber}
                    </button>
                  ))}
                </div>
                {totalPages ? (
                  <span className="cg-history-page-summary" aria-live="polite">
                    Page {campaignHistoryState.currentPage} of {totalPages}
                  </span>
                ) : null}
                <button
                  type="button"
                  className="cg-history-next-button"
                  onClick={onHistoryNextPage}
                  disabled={!campaignHistoryState.hasMore || campaignHistoryState.loadingPage}
                >
                  {campaignHistoryState.loadingPage ? "Loading..." : "Next"}
                </button>
              </div>
            ) : null}
          </div>
        </section>
      </div>
    </main>
  );
}
